import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { FaFacebook, FaLinkedin, FaTiktok, FaMicrosoft, FaGoogle, FaAmazon, FaSnapchatGhost, FaReddit, FaPinterest, FaTwitter, FaApple, FaSpotify, FaYahoo } from 'react-icons/fa';
import { SiGoogleads } from 'react-icons/si';
import { Trash2, Plus, CheckCircle2, Search, Clock, Globe, MapPin, TrendingUp, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Platforms = () => {
  const [platforms, setPlatforms] = useState([]);
  const [supportedPlatforms, setSupportedPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [platformName, setPlatformName] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [connectedRes, supportedRes] = await Promise.all([
        axios.get(`${API}/platforms`, { withCredentials: true }),
        axios.get(`${API}/platforms/supported`, { withCredentials: true })
      ]);
      setPlatforms(connectedRes.data);
      setSupportedPlatforms(supportedRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load platforms');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectPlatform = async () => {
    if (!selectedPlatform || !platformName) {
      toast.error('Please select a platform and enter a name');
      return;
    }

    try {
      await axios.post(`${API}/platforms`, {
        platform_type: selectedPlatform.platform_type,
        platform_name: platformName
      }, { withCredentials: true });
      
      toast.success(`${selectedPlatform.name} connected successfully`);
      setIsDialogOpen(false);
      setSelectedPlatform(null);
      setPlatformName('');
      fetchData();
    } catch (error) {
      console.error('Error connecting platform:', error);
      toast.error(error.response?.data?.detail || 'Failed to connect platform');
    }
  };

  const handleDeletePlatform = async (platformId) => {
    try {
      await axios.delete(`${API}/platforms/${platformId}`, {
        withCredentials: true
      });
      toast.success('Platform disconnected');
      fetchData();
    } catch (error) {
      console.error('Error deleting platform:', error);
      toast.error('Failed to disconnect platform');
    }
  };

  const getPlatformIcon = (type) => {
    const iconMap = {
      google_ads: SiGoogleads,
      dv360: SiGoogleads,
      google_analytics: FaGoogle,
      cm360: FaGoogle,
      meta_ads: FaFacebook,
      instagram_ads: FaFacebook,
      linkedin_ads: FaLinkedin,
      tiktok_ads: FaTiktok,
      microsoft_ads: FaMicrosoft,
      ttd: TrendingUp, // Use TrendingUp for The Trade Desk
      amazon_dsp: FaAmazon,
      amazon_ads: FaAmazon,
      snapchat_ads: FaSnapchatGhost,
      reddit_ads: FaReddit,
      pinterest_ads: FaPinterest,
      twitter_ads: FaTwitter,
      apple_search_ads: FaApple,
      spotify_ads: FaSpotify,
      yahoo_japan_ads: FaYahoo,
      youtube_ads: BarChart3, // Use BarChart3 for YouTube
    };
    return iconMap[type] || Globe;
  };

  const categories = supportedPlatforms.categories || [];
  const regions = supportedPlatforms.regions || [];

  const filteredPlatforms = (supportedPlatforms.platforms || []).filter(platform => {
    const matchesCategory = selectedCategory === 'all' || platform.category === selectedCategory;
    const matchesRegion = selectedRegion === 'all' || platform.region === selectedRegion;
    const matchesSearch = searchQuery === '' || 
      platform.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      platform.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesRegion && matchesSearch;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading platforms...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="platforms-page">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">Connected Platforms</h1>
            <p className="text-muted-foreground">
              {supportedPlatforms.total_platforms || 0} platforms available • {platforms.length} connected
            </p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-platform-button" className="bg-primary hover:bg-primary/90">
                <Plus className="w-4 h-4 mr-2" />
                Connect Platform
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Connect Advertising Platform</DialogTitle>
              </DialogHeader>

              {!selectedPlatform ? (
                <div className="space-y-4">
                  {/* Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search platforms..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Filters */}
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Label>Category</Label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="all">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex-1">
                      <Label>Region</Label>
                      <select
                        value={selectedRegion}
                        onChange={(e) => setSelectedRegion(e.target.value)}
                        className="w-full h-10 px-3 rounded-md border border-input bg-background"
                      >
                        <option value="all">All Regions</option>
                        {regions.map(reg => (
                          <option key={reg} value={reg}>{reg}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Platform Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                    {filteredPlatforms.map((platform) => {
                      const Icon = getPlatformIcon(platform.platform_type);
                      const isConnected = platforms.some(p => p.platform_type === platform.platform_type);
                      
                      return (
                        <Card
                          key={platform.platform_type}
                          className={`p-4 cursor-pointer transition-all hover:border-primary/50 ${
                            isConnected ? 'opacity-50' : ''
                          }`}
                          onClick={() => !isConnected && setSelectedPlatform(platform)}
                        >
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Icon className="w-5 h-5 text-primary" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-sm truncate">{platform.name}</h4>
                              <div className="flex items-center gap-1 mt-1">
                                <Badge variant="secondary" className="text-xs">
                                  {platform.category}
                                </Badge>
                              </div>
                              <div className="flex items-center text-xs text-muted-foreground mt-1">
                                <MapPin className="w-3 h-3 mr-1" />
                                {platform.region}
                              </div>
                              {isConnected && (
                                <div className="flex items-center text-xs text-green-500 mt-1">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Connected
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>

                  {filteredPlatforms.length === 0 && (
                    <div className="text-center py-8 text-muted-foreground">
                      No platforms found matching your filters
                    </div>
                  )}
                </div>
              ) : (
                /* Platform Configuration */
                <div className="space-y-4">
                  <Button
                    variant="ghost"
                    onClick={() => setSelectedPlatform(null)}
                    className="mb-4"
                  >
                    ← Back to platforms
                  </Button>
                  
                  <Card className="p-6">
                    <div className="flex items-center space-x-4 mb-4">
                      {(() => {
                        const Icon = getPlatformIcon(selectedPlatform.platform_type);
                        return (
                          <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                            <Icon className="w-8 h-8 text-primary" />
                          </div>
                        );
                      })()}
                      <div>
                        <h3 className="text-xl font-bold">{selectedPlatform.name}</h3>
                        <div className="flex gap-2 mt-1">
                          <Badge>{selectedPlatform.category}</Badge>
                          <Badge variant="outline">{selectedPlatform.region}</Badge>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="platform_name">Connection Name *</Label>
                        <Input
                          id="platform_name"
                          value={platformName}
                          onChange={(e) => setPlatformName(e.target.value)}
                          placeholder="e.g., Main Account, US Market, APAC Campaign"
                          className="mt-1"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Give this connection a memorable name
                        </p>
                      </div>

                      <div className="p-4 bg-muted rounded-lg">
                        <div className="flex items-start space-x-2">
                          <Clock className="w-5 h-5 text-primary mt-0.5" />
                          <div>
                            <h4 className="font-bold text-sm">Using Mock Data</h4>
                            <p className="text-xs text-muted-foreground mt-1">
                              This platform will use simulated data for demonstration. Real API connection can be configured later in settings.
                            </p>
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleConnectPlatform}
                        className="w-full"
                        disabled={!platformName}
                      >
                        Connect {selectedPlatform.name}
                      </Button>
                    </div>
                  </Card>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        {/* Connected Platforms Grid */}
        {platforms.length === 0 ? (
          <Card className="p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-xl font-bold mb-2">No Platforms Connected</h3>
              <p className="text-muted-foreground mb-6">
                Connect your first advertising platform from our library of {supportedPlatforms.total_platforms || 0} platforms
              </p>
              <Button onClick={() => setIsDialogOpen(true)} data-testid="empty-state-connect-button">
                Browse {supportedPlatforms.total_platforms || 0} Platforms
              </Button>
            </div>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {platforms.map((platform) => {
              const Icon = getPlatformIcon(platform.platform_type);
              const platformInfo = (supportedPlatforms.platforms || []).find(
                p => p.platform_type === platform.platform_type
              );
              
              return (
                <Card key={platform.platform_id} data-testid={`platform-card-${platform.platform_id}`} className="p-6 hover:border-primary/50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold truncate">{platform.platform_name}</h3>
                        <p className="text-sm text-muted-foreground truncate">
                          {platformInfo?.name || platform.platform_type}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      data-testid={`delete-platform-${platform.platform_id}`}
                      onClick={() => handleDeletePlatform(platform.platform_id)}
                      className="text-destructive hover:bg-destructive/10"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Status</span>
                      <div className="flex items-center space-x-2">
                        {platform.api_connection_status === 'mock' ? (
                          <>
                            <Clock className="w-4 h-4 text-yellow-500" />
                            <span className="text-yellow-500">Mock Data</span>
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 text-green-500" />
                            <span className="text-green-500">Connected</span>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Category</span>
                      <Badge variant="secondary">{platformInfo?.category || 'N/A'}</Badge>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Region</span>
                      <span className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {platformInfo?.region || 'Global'}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Platform Categories Overview */}
        <Card className="p-6 mt-8">
          <h3 className="text-lg font-bold mb-4">Platform Categories</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {categories.map((category) => {
              const count = (supportedPlatforms.platforms || []).filter(
                p => p.category === category
              ).length;
              return (
                <div key={category} className="p-3 bg-muted rounded-lg">
                  <div className="font-bold text-sm">{category}</div>
                  <div className="text-xs text-muted-foreground">{count} platforms</div>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Platforms;
